<template>
  <SubpageShell
    :eyebrow="trans.get('landing.subpages.faq.eyebrow')"
    :title-prefix="trans.get('landing.subpages.faq.title_prefix')"
    :title-grad="trans.get('landing.subpages.faq.title_grad')"
    :title-suffix="trans.get('landing.subpages.faq.title_suffix')"
    :cta-lede="trans.get('landing.subpages_shared.cta_lede_quiz_match')"
    nav-active="help"
  >
    <template #lede>
      {{ trans.get('landing.subpages.faq.lede_prefix') }}
      <a href="mailto:support@viluu.de">{{ trans.get('landing.subpages.faq.lede_link') }}</a>
      {{ trans.get('landing.subpages.faq.lede_suffix') }}
    </template>

    <section
      class="sec faq-page-sec"
      id="faq"
      :aria-busy="loading"
    >
      <div
        class="faq-grid"
        :class="{ 'is-loading': loading }"
      >
        <details
          v-for="question in allQuestions"
          :key="question.id"
          class="faq-item"
          @click="toggleFaq"
        >
          <summary>{{ question.question }}</summary>
          <div
            class="faq-answer"
            v-html="question.answer"
          ></div>
        </details>
      </div>
    </section>
  </SubpageShell>
</template>

<script>
import SubpageShell from './SubpageShell.vue';

export default {
  name: 'FaqPage',
  components: { SubpageShell },
  data() {
    return {
      faqCategories: [],
      // While true the grid reserves space (see .is-loading) so the CTA band +
      // footer below don't jump down once the questions load in.
      loading: true,
    };
  },
  computed: {
    allQuestions() {
      return this.faqCategories.flatMap((category) => category.questions);
    },
  },
  mounted() {
    this.fetchFaq();
  },
  methods: {
    fetchFaq() {
      axios
        .get(zRoute('ajax.faq_categories.index'))
        .then((response) => {
          this.faqCategories = response.data.data;
        })
        .finally(() => {
          // Release the reserved height once the request settles — on error
          // too, so an empty grid never stays padded out.
          this.loading = false;
        });
    },
    // Toggle the <details> from a click anywhere in the item, not just the
    // summary. Clicks on links inside the answer are left to behave normally.
    toggleFaq(event) {
      if (event.target.closest('a')) {
        return;
      }
      event.preventDefault();
      event.currentTarget.open = !event.currentTarget.open;
    },
  },
};
</script>

<style scoped lang="scss">
.faq-page-sec {
  padding-top: 30px;
}

.faq-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 40px;
  align-items: start;
}

// Approximate height reservation while the async questions load, so the grid
// doesn't collapse to zero and shove the CTA band / footer down on arrival.
// Roughly a screenful keeps the in-viewport area stable; dropped once loaded.
.faq-grid.is-loading {
  min-height: 60vh;
}

.faq-item {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 22px 26px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  align-self: start;
  user-select: none;
}

.faq-item:hover {
  border-color: rgba(198, 62, 124, 0.3);
}

.faq-item summary {
  font-family: var(--display);
  font-weight: 700;
  font-size: 17px;
  letter-spacing: -0.01em;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  line-height: normal;
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item summary::after {
  content: '+';
  font-size: 24px;
  font-weight: 400;
  color: var(--pink);
  transition: transform 0.25s ease;
  font-family: var(--sans);
}

.faq-item[open] summary::after {
  transform: rotate(45deg);
}

.faq-item p,
.faq-answer {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-dim);
}

.faq-answer p {
  margin: 0 0 10px;
}

.faq-answer p:last-child {
  margin-bottom: 0;
}

.faq-item p strong,
.faq-answer strong {
  color: var(--ink);
}

.faq-answer a {
  color: var(--pink);
  font-weight: 600;
}

.faq-item h6 {
  margin: 14px 0 6px;
  font-family: var(--sans);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--pink);
}

.faq-item ol {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
  counter-reset: s;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.faq-item ol li {
  position: relative;
  padding: 3px 0 3px 30px;
  counter-increment: s;
  font-size: 13.5px;
  color: var(--ink-dim);
  line-height: 1.5;
}

.faq-item ol li::before {
  content: counter(s);
  position: absolute;
  left: 0;
  top: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--coral), var(--pink));
  color: #fff;
  font-family: var(--display);
  font-weight: 700;
  font-size: 11px;
  display: grid;
  place-items: center;
  line-height: 1;
}

.faq-item .note {
  margin-top: 14px;
  padding: 14px 16px 14px 48px;
  background: linear-gradient(135deg, #fff5ec 0%, #ffe9d6 100%);
  border: 1px solid rgba(241, 124, 8, 0.2);
  border-radius: 14px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ink);
  position: relative;
  box-shadow: 0 4px 12px -6px rgba(241, 124, 8, 0.25);
}

.faq-item .note::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 14px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--coral), var(--pink));
  box-shadow: 0 4px 10px -2px rgba(198, 62, 124, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.faq-item .note::after {
  content: '!';
  position: absolute;
  left: 14px;
  top: 14px;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  color: #fff;
  font-family: var(--display);
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
}

.faq-item .note strong {
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.005em;
}

@media (max-width: 1100px) {
  .faq-grid {
    grid-template-columns: 1fr;
  }
}
</style>
